<template>
  <div class="agent-panel">
    <agent-thread-list class="agent-panel__threads" />

    <section class="agent-panel__main">
      <agent-thread-view v-if="agentChat.activeThread" :thread="agentChat.activeThread" />
      <div v-else class="agent-panel__placeholder">
        <div v-if="agentChat.activeLoading">Loading…</div>
        <template v-else>
          <div class="agent-panel__placeholder-title">Pick a thread, or start one</div>
          <fs-button size="small" accent @click="agentChat.newThread()">New thread</fs-button>
        </template>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue';
import { useAgentChat } from '@/store/agent-chat';
import { useShortcutLayer } from '@lib/shortcuts';
import AgentThreadList from './agent-thread-list.vue';
import AgentThreadView from './agent-thread-view.vue';
import FsButton from '@components/buttons/fs-button.vue';

const agentChat = useAgentChat();

onMounted(async () => {
  if (!agentChat.threadsLoaded) await agentChat.loadThreads();
  // Land on the thread they were last working in rather than an empty pane.
  await agentChat.openLatest();
});

// A layer of its own, not bindings on the page: these are bare letters, and the page under the
// panel has its own claim on them — `n` opens a create form on half the pages in the PLM. The
// component only exists while the agent tab is showing, so the layer's life is the right one.
const { register } = useShortcutLayer(true, 'Agent');

/** Along the tab strip, so the keys move the way the eye reads it. */
const step = async (delta: number) => {
  const threads = agentChat.activeThreads;
  if (!threads.length) return;

  // An archived thread stays open and readable but is not in the strip, so there is no
  // neighbour to step to. The keys enter the strip at the end they are pointing away from.
  const current = threads.findIndex((t) => t.id === agentChat.activeThread?.summary.id);
  if (current === -1) {
    await agentChat.openThread(delta > 0 ? threads[0].id : threads[threads.length - 1].id);
    return;
  }

  // Stops at the ends rather than wrapping: the strip has visible ends, and a key that jumped
  // from the last thread to the first would look like it had opened the wrong one.
  const next = current + delta;
  if (next < 0 || next >= threads.length) return;
  await agentChat.openThread(threads[next].id);
};

register('n', () => agentChat.newThread(), { description: 'New thread' });
register('arrowleft', () => step(-1), { description: 'Previous thread' });
register('arrowright', () => step(1), { description: 'Next thread' });

// The same thing the ✕ on the tab does, including leaving the thread open — archiving closes a
// thread to new messages, it does not take it off the screen.
register('x', () => agentChat.archive(agentChat.activeThread!.summary.id), {
  enabled: () => agentChat.activeThread != null && !agentChat.activeThread.summary.isArchived,
  description: 'Archive this thread',
});
</script>

<style scoped lang="scss">
@import '@styles/variables.scss';

.agent-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;

  &__threads {
    flex: none;
  }

  &__main {
    flex: 1;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  &__placeholder {
    margin: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.4rem;
    font-size: 1.4rem;
    color: rgba($c-ink, 0.5);
  }

  &__placeholder-title {
    font-family: 'Montserrat', sans-serif;
    font-size: 1.6rem;
    font-weight: 600;
    color: $c-ink;
  }
}
</style>
